import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Button, ButtonGroup, Form, Alert, Spinner, Badge, OverlayTrigger, Popover } from 'react-bootstrap';
import supervisorApi from '../../services/supervisorApi';
import taskApi from '../../services/taskApi';
import EditTaskModal from '../EditTaskModal';
import useLanguage from '../../hooks/useLanguage';
import { formatDate, formatShortDateTime } from '../../utils/dateFormatter';

const PRESETS = ['last7', 'thisMonth', 'lastMonth', 'custom'];

// Brand palette (HOST_ME_UI_DESIGN_SYSTEM.md): green = within, orange = over, grey = not measured.
const COLOR = { within: '#48b58d', over: '#ff9f53', rest: '#ced4da', muted: '#6c757d', ink: '#090040' };

// Timing codes from the server (services/cleaningPerformance.js TIMING) → the words for them.
const TIMING_KEYS = {
  timed: null,
  no_target: 'tasks.performance.timing.noTarget',
  missing_start: 'tasks.performance.timing.missingStart',
  too_short: 'tasks.performance.timing.tooShort',
  too_long: 'tasks.performance.timing.tooLong'
};

/**
 * «تقارير الأداء» — how each cleaner did over a period (tasks/cleaning-performance-report.md step 5 + 8).
 *
 * Everything is decided on the server: the period (presets resolved in Riyadh), who is credited, which
 * cleans count and why the others do not. This tab only words the codes and draws the figures: a strip
 * with the team's answer (within-target share, average vs target, volume), one timing bar per cleaner
 * (length = cleans, colour = within / over / not measured, in the server's order: most cleans first —
 * a share order would let one lucky clean top the list), and the unattributed cleans as
 * a data-quality line rather than a cleaner. A row opens that cleaner's cleans; a ticket opens the task
 * dialog, where «سجل النظافة» corrects it.
 *
 * Quantities are Western digits in Arabic too — the founder's rule for the supervisor tabs (see
 * AttentionTab `num`). Dates keep the shared formatters.
 */
const PerformanceTab = ({ refreshSignal = 0 }) => {
  const { t, isArabic, language } = useLanguage();
  const [preset, setPreset] = useState('thisMonth');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [query, setQuery] = useState({ period: 'thisMonth' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openCleaner, setOpenCleaner] = useState(null);   // userId | 'unattributed' | null
  const [editingTask, setEditingTask] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  const num = (n) => (n === null || n === undefined ? '—' : String(n));
  const minutes = (n) => (n === null || n === undefined ? '—' : `${n} ${t('tasks.card.minutes')}`);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const resp = await supervisorApi.getCleaningPerformance(query);
      setData(resp.data.data);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  const cleaners = data?.cleaners || [];
  const busiest = Math.max(0, ...cleaners.map(c => c.cleans));

  const choosePreset = (key) => {
    setPreset(key);
    setOpenCleaner(null);
    if (key !== 'custom') setQuery({ period: key });
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    setOpenCleaner(null);
    setQuery({ from: customFrom, to: customTo });
  };

  const openTask = async (taskId) => {
    setOpeningId(taskId);
    try {
      const resp = await taskApi.getTask(taskId);
      if (resp?.data?.task?._id) setEditingTask(resp.data.task);
    } finally {
      setOpeningId(null);
    }
  };

  const toggle = (key) => setOpenCleaner(openCleaner === key ? null : key);

  const nameOf = (userId) => data?.cleaners.find(c => c.userId === userId)?.name || '—';

  const cleansFor = (key) => (data?.cleans || []).filter(c => (key === 'unattributed'
    ? c.cleanerUserIds.length === 0
    : c.cleanerUserIds.includes(key)));

  const legendDot = (color) => <span className="hm-legend-dot" style={{ background: color }} />;

  // Rendered twice: in the table header (desktop) and under the team bar (phone) — one source.
  const legend = (
    <>
      {legendDot(COLOR.within)}{t('tasks.performance.cols.within')}
      {' '}{legendDot(COLOR.over)}{t('tasks.performance.cols.over')}
      {' '}{legendDot(COLOR.rest)}{t('tasks.performance.cols.notMeasured')}
    </>
  );

  /**
   * The timing bar: length = this row's cleans against the busiest cleaner's, segments = within (green)
   * / over (orange) / not measured (grey). The counts sit beside it so colour is never the only carrier.
   */
  const renderTimingBar = (row, max) => {
    const rest = row.cleans - row.withinTarget - row.overTarget;
    const width = max > 0 ? `${Math.max(6, Math.round((row.cleans / max) * 100))}%` : '100%';
    const label = `${t('tasks.performance.cols.within')} ${row.withinTarget} · ${t('tasks.performance.cols.over')} ${row.overTarget} · ${t('tasks.performance.cols.notMeasured')} ${rest}`;
    return (
      <div className="hm-bar-wrap">
        <div className="hm-bar-track">
          <div className="hm-bar" style={{ width }} role="img" aria-label={label} title={label}>
            {row.withinTarget > 0 && <span className="hm-bar-seg" style={{ flex: row.withinTarget, background: COLOR.within }} />}
            {row.overTarget > 0 && <span className="hm-bar-seg" style={{ flex: row.overTarget, background: COLOR.over }} />}
            {rest > 0 && <span className="hm-bar-seg" style={{ flex: rest, background: COLOR.rest }} />}
          </div>
        </div>
        <span className="hm-bar-counts">
          <span style={{ color: COLOR.within }}>{row.withinTarget}</span>
          {' · '}<span style={{ color: COLOR.over }}>{row.overTarget}</span>
          {' · '}<span className="text-muted">{rest}</span>
        </span>
      </div>
    );
  };

  /**
   * The team's answer, before the table: the within-target share with the team bar, and the average.
   * Two figures only — the volume and the alone / with-partner split are the table's (founder, 23 Sep).
   * No "distance from target" line: the bar already answers "on target?" (founder, v1.2.31 — less is
   * more). Reads `total`, so unattributed cleans count — they happened.
   */
  const renderStrip = (total) => {
    const measured = total.withinTarget + total.overTarget;
    const pct = measured ? Math.round((100 * total.withinTarget) / measured) : null;
    return (
      <div className="hm-perf-strip">
        <div className="hm-perf-figure hm-perf-figure-wide">
          {/* The count leads at any volume («3 من 5»), the % sits beside it in grey — a supervisor reads
              "how many" first, the share second (founder, 25 Sep, v1.2.33). The bar carries the judgement. */}
          <div className="hm-perf-value" style={{ color: pct === null ? COLOR.muted : COLOR.ink }}>
            {pct === null ? '—' : (
              <>
                <bdi dir={isArabic ? 'rtl' : 'ltr'}>{`${total.withinTarget} ${t('tasks.performance.strip.of')} ${measured}`}</bdi>
                <small> <bdi dir="ltr">{`${pct}%`}</bdi></small>
              </>
            )}
          </div>
          <div className="hm-perf-label">{t('tasks.performance.strip.within')}</div>
          {pct === null && <div className="hm-perf-sub">{t('tasks.performance.strip.noMeasured')}</div>}
          {renderTimingBar(total, total.cleans)}
          {/* On a phone the table header (and its legend) is hidden, so the legend sits under the team bar. */}
          <div className="hm-legend hm-legend-mobile d-sm-none">{legend}</div>
        </div>
        <div className="hm-perf-figure hm-perf-figure-avg">
          <div className="hm-perf-value">
            {num(total.averageMinutes)}
            {total.averageMinutes !== null && <small> {t('tasks.card.minutes')}</small>}
          </div>
          <div className="hm-perf-label">{t('tasks.performance.strip.average')}</div>
        </div>
      </div>
    );
  };

  // The cell classes (hm-cl-*, hm-td-*) carry no style on desktop; under 576px TasksPage.css turns each
  // row into a stacked grid and places the cells by them (tasks/performance-tab-mobile.md).
  const renderCleansTable = (key) => (
    <Table size="sm" className="mb-0 hm-perf-cleans" style={{ background: '#faf8fd' }}>
      <tbody>
        {cleansFor(key).map(c => {
          const partner = key === 'unattributed' ? [] : c.cleanerUserIds.filter(id => id !== key);
          return (
            <tr key={c.taskId}>
              <td className="small hm-cl-date">{formatShortDateTime(c.completedAt, language)}</td>
              <td className="small hm-cl-unit">
                {c.unit ? `${c.unit.name}${c.unit.code ? ` · ${c.unit.code}` : ''}` : '—'}
              </td>
              <td className="small hm-cl-timing">
                {TIMING_KEYS[c.timingStatus]
                  ? <Badge bg="light" text="dark">{t(TIMING_KEYS[c.timingStatus])}</Badge>
                  : `${minutes(c.durationMinutes)} / ${minutes(c.targetMinutes)}`}
                {['no_target', 'too_long'].includes(c.timingStatus) && ` ${minutes(c.durationMinutes)}`}
              </td>
              <td className="small text-muted hm-cl-partner">
                {partner.length ? `${t('tasks.performance.with')} ${partner.map(nameOf).join(' · ')}` : ''}
              </td>
              <td className="small hm-cl-ticket">
                {c.corrected && <Badge bg="warning" text="dark" style={{ marginInlineEnd: 4 }}>{t('tasks.performance.corrected')}</Badge>}
                {c.ticketId && (
                  <Button variant="link" size="sm" className="p-0" onClick={() => openTask(c.taskId)}
                    disabled={openingId === c.taskId}>
                    {c.ticketId}
                  </Button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );

  /**
   * «أوقات تسجيل المغادرة» (tasks/checkout-times-card.md) — the server buckets and labels everything
   * (Riyadh windows, the departure rule); this only words the window keys and draws one bar per window.
   * Bar length = share of RECORDED checkouts, the count beside it so the bar is never the only carrier.
   * Read defensively: a web deploy ahead of the backend renders nothing rather than failing.
   */
  const renderCheckoutTimes = (ct) => {
    // No cleans in the period: the report card above already says so — one empty state, not two.
    if (!ct || data.total.cleans === 0) return null;
    const note = (
      <Popover id="checkout-times-about">
        <Popover.Header as="h3" className="small">{t('tasks.performance.checkoutTimes.about')}</Popover.Header>
        <Popover.Body className="small">{t('tasks.performance.checkoutTimes.note')}</Popover.Body>
      </Popover>
    );
    return (
      <Card className="mb-3">
        <Card.Header className="small d-flex align-items-center justify-content-between">
          <span><strong>{t('tasks.performance.checkoutTimes.title')}</strong></span>
          <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={note}>
            <Button variant="link" size="sm" className="p-0 text-muted" aria-label={t('tasks.performance.checkoutTimes.about')}>
              <i className="bi bi-info-circle" />
            </Button>
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <p className="text-muted small mb-3">{t('tasks.performance.checkoutTimes.intro')}</p>
          {ct.recorded === 0 ? (
            <p className="text-muted text-center py-2 mb-0">{t('tasks.performance.checkoutTimes.empty')}</p>
          ) : (
            <div className="hm-checkout-windows">
              {ct.windows.map(w => {
                const pct = Math.round((100 * w.count) / ct.recorded);
                const label = t(`tasks.performance.checkoutTimes.windows.${w.key}`);
                return (
                  <div className="hm-checkout-window" key={w.key}>
                    <span className="hm-checkout-label">{label}</span>
                    <div className="hm-bar-track">
                      <div className="hm-bar" role="img" aria-label={`${label}: ${w.count} (${pct}%)`}
                        style={{ width: w.count ? `${Math.max(3, pct)}%` : 0, background: COLOR.ink }} />
                    </div>
                    <span className="hm-bar-counts">
                      <strong>{num(w.count)}</strong>{' '}<span className="text-muted"><bdi dir="ltr">{`${pct}%`}</bdi></span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="hm-checkout-foot small text-muted">
            <span>{t('tasks.performance.checkoutTimes.recorded')}: <strong>{num(ct.recorded)}</strong></span>
            <span>{t('tasks.performance.checkoutTimes.missing')}: <strong>{num(ct.missing)}</strong></span>
            <span>{t('tasks.performance.checkoutTimes.byGuest')}: <strong>{num(ct.bySource?.guest)}</strong></span>
            <span>{t('tasks.performance.checkoutTimes.byOps')}: <strong>{num(ct.bySource?.manual)}</strong></span>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const aboutPopover = (
    <Popover id="performance-about">
      <Popover.Header as="h3" className="small">{t('tasks.performance.about')}</Popover.Header>
      <Popover.Body className="small">{t('tasks.performance.footnote')}</Popover.Body>
    </Popover>
  );

  const onRowKey = (e, key) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle(key);
    }
  };

  return (
    <div dir={isArabic ? 'rtl' : 'ltr'}>
      <p className="text-muted small mb-3">{t('tasks.performance.intro')}</p>

      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <ButtonGroup size="sm" className="hm-perf-presets">
          {PRESETS.map(key => (
            <Button key={key} variant={preset === key ? 'primary' : 'outline-primary'} onClick={() => choosePreset(key)}>
              {t(`tasks.performance.periods.${key}`)}
            </Button>
          ))}
        </ButtonGroup>
        {preset === 'custom' && (
          <div className="hm-perf-custom">
            <Form.Control type="date" size="sm" style={{ width: 'auto' }} value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)} aria-label={t('tasks.performance.from')} />
            <Form.Control type="date" size="sm" style={{ width: 'auto' }} value={customTo}
              onChange={(e) => setCustomTo(e.target.value)} aria-label={t('tasks.performance.to')} />
            <Button size="sm" onClick={applyCustom} disabled={!customFrom || !customTo}>
              {t('tasks.performance.apply')}
            </Button>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="danger" className="d-flex justify-content-between align-items-center">
          <span>{t('tasks.performance.error')}</span>
          <Button size="sm" variant="outline-danger" onClick={load}>{t('tasks.attention.retry')}</Button>
        </Alert>
      )}

      {loading && !data && <div className="text-center py-4"><Spinner animation="border" /></div>}

      {data && (
        <Card className="mb-3">
          <Card.Header className="small text-muted d-flex align-items-center justify-content-between">
            <span>
              {formatDate(data.period.from, {}, language)} – {formatDate(data.period.to, {}, language)}
              {' · '}{t('tasks.performance.cleansInPeriod')}: {num(data.total.cleans)}
            </span>
            {data.total.cleans > 0 && (
              <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={aboutPopover}>
                <Button variant="link" size="sm" className="p-0 text-muted" aria-label={t('tasks.performance.about')}>
                  <i className="bi bi-info-circle" />
                </Button>
              </OverlayTrigger>
            )}
          </Card.Header>
          <Card.Body className="p-0">
            {data.total.cleans === 0 ? (
              <p className="text-muted text-center py-4 mb-0">{t('tasks.performance.empty')}</p>
            ) : (
              <>
                {renderStrip(data.total)}
                {cleaners.length === 0 ? (
                  <p className="text-muted text-center py-4 mb-0">{t('tasks.performance.noAttributed')}</p>
                ) : (
                  <Table hover responsive className="mb-0 align-middle hm-perf-table">
                    <thead>
                      <tr className="small text-muted">
                        <th>{t('tasks.performance.cols.cleaner')}</th>
                        <th>{t('tasks.performance.cols.cleans')}</th>
                        <th className="hm-perf-bar-col">
                          {t('tasks.performance.cols.timing')}
                          <span className="hm-legend">{legend}</span>
                        </th>
                        <th>{t('tasks.performance.cols.average')}</th>
                        <th>{t('tasks.performance.cols.soloPaired')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cleaners.map(row => (
                        <React.Fragment key={row.userId}>
                          {/* data-label = the header the phone layout hides, drawn by ::before there. */}
                          <tr className="hm-perf-row" role="button" tabIndex={0}
                            aria-expanded={openCleaner === row.userId}
                            onClick={() => toggle(row.userId)} onKeyDown={(e) => onRowKey(e, row.userId)}>
                            <td className="fw-semibold hm-td-name">{row.name || '—'}</td>
                            <td className="hm-td-cleans" data-label={t('tasks.performance.cols.cleans')}>
                              {num(row.cleans)}
                              <i className="bi bi-chevron-down hm-perf-chevron" aria-hidden="true" />
                            </td>
                            <td className="hm-perf-bar-col hm-td-bar">{renderTimingBar(row, busiest)}</td>
                            <td className="hm-td-avg" data-label={t('tasks.performance.cols.average')}>{minutes(row.averageMinutes)}</td>
                            <td className="text-muted small hm-td-pair" data-label={t('tasks.performance.cols.soloPaired')}>
                              {num(row.solo)} / {num(row.paired)}
                            </td>
                          </tr>
                          {openCleaner === row.userId && (
                            <tr className="hm-perf-detail"><td colSpan={5} className="p-0">{renderCleansTable(row.userId)}</td></tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                )}
                {data.unattributed.cleans > 0 && (
                  <>
                    <div className="hm-perf-unattributed">
                      <i className="bi bi-exclamation-triangle" />
                      <span className="flex-grow-1">
                        <strong>{num(data.unattributed.cleans)}</strong> {t('tasks.performance.unattributedLine')}
                      </span>
                      <Button variant="link" size="sm" className="p-0" onClick={() => toggle('unattributed')}>
                        {t(openCleaner === 'unattributed' ? 'tasks.performance.hide' : 'tasks.performance.show')}
                      </Button>
                    </div>
                    {openCleaner === 'unattributed' && renderCleansTable('unattributed')}
                  </>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {data && renderCheckoutTimes(data.checkoutTimes)}

      <EditTaskModal
        show={!!editingTask}
        task={editingTask}
        onHide={() => { setEditingTask(null); load(); }}
        onTaskUpdated={() => { setEditingTask(null); load(); }}
      />
    </div>
  );
};

export default PerformanceTab;
